import { graphql, useStaticQuery } from 'gatsby';
import { useContext, useEffect, useState } from 'react';
import { GlobalContext } from '../context/GlobalContext';
import {
    Category,
    ICategories,
    ICategory,
    IMetadata,
    ISecciones,
    ISubcategory,
    Subcategory,
} from '../interfaces/Article.interface';
import useBrowserMode from '../shared/hooks/useBrowserMode';

export const useGetTree = () => {
    const { window } = useBrowserMode();
    const { selectedTab } = useContext(GlobalContext);
    const [data, setData] = useState<ICategory>();

    const { allContentfulTemplate }: { allContentfulTemplate: ICategories } =
        useStaticQuery(graphql`
            {
                allContentfulTemplate(
                    filter: {
                        slug: {
                            in: ["categorias_personas", "categorias_empresas"]
                        }
                    }
                ) {
                    nodes {
                        h2
                        slug
                        button {
                            copy
                            url
                        }
                        referencias {
                            span
                            span2
                            h3
                            url
                            slug
                            color
                            icon
                            tipoUsuario
                            image {
                                title
                                file {
                                    url
                                }
                            }
                            secciones {
                                h2
                                url
                                tipoUsuario
                                button {
                                    copy
                                }
                                articulo {
                                    h2
                                    slug
                                    url
                                }
                            }
                            metadata {
                                metaTitulo
                                metaDescripcion
                                index
                                metaImagen {
                                    gatsbyImageData
                                    file {
                                        url
                                    }
                                }
                            }
                        }
                    }
                }
            }
        `);

    useEffect(() => {
        setData(
            allContentfulTemplate.nodes.find(
                (node) =>
                    node.slug ===
                    (selectedTab.get === 'personas'
                        ? 'categorias_personas'
                        : 'categorias_empresas')
            )
        );
    }, [selectedTab.get]);

    const getMetaDataSEO = (metadata: IMetadata) => {
        if (!metadata) return null;
        const { metaTitulo, metaDescripcion, metaImagen, index } = metadata;
        return {
            metaTitle: metaTitulo,
            metaDescription: metaDescripcion,
            metaImage: metaImagen,
            index,
        };
    };

    const getSections = (sections: ISecciones[]) => {
        return (
            sections?.map(({ h2, url, articulo, tipoUsuario }) => ({
                name: h2,
                url,
                userType: tipoUsuario ?? 'personas',
                articles:
                    articulo?.map(({ h2, slug, url }) => ({
                        name: h2,
                        slug,
                        url,
                    })) ?? [],
            })) ?? []
        );
    };

    const getSubcategories = (subcategories: ISubcategory[]) => {
        return (
            subcategories?.map(({ span, referencias, url, metadata }) => ({
                name: span,
                desc: referencias?.[0]?.h3 ?? '',
                url,
                sections: getSections(referencias?.[0]?.secciones ?? []),
                metaDataSEO: getMetaDataSEO(metadata),
            })) ?? []
        );
    };

    const categories = data?.referencias.map(
        ({
            span,
            h3,
            color,
            icon,
            image,
            url,
            tipoUsuario,
            secciones,
            referencias,
            metadata,
        }) => ({
            name: span,
            desc: h3 ?? '',
            color,
            icon: icon ?? '',
            image: image ?? '',
            url,
            userType: tipoUsuario ?? 'personas',
            sections: getSections(secciones),
            subcategories: getSubcategories(
                referencias?.[0]?.referencias ?? []
            ),
            metaDataSEO: getMetaDataSEO(metadata),
        })
    ) as Category[];

    const currentUrl =
        window?.location?.hostname !== 'localhost' ||
        window?.location?.port === '9000'
            ? window?.location?.pathname.split('/').slice(2)
            : window?.location?.pathname.split('/').slice(1);

    const currentCategory = categories?.find(
        (category) => category.url?.replace('/', '') === currentUrl?.[0]
    ) as Category;

    const currentSubcategory = currentCategory?.sections?.find(
        (subCategory) =>
            subCategory.url.split('/')[2]?.replace('/', '') === currentUrl[1]
    ) as Subcategory;

    return {
        title: data?.h2 ?? '',
        link: data?.button[0],
        categories,
        selectedCategory: currentCategory,
        selectedSubCategory: currentSubcategory,
    };
};
